<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useAuthStore } from "@/stores/useAuthStore";
import { useMenuStore } from "@/stores/useMenuStore";
import { useRouter, useRoute } from "vue-router";
import { CircleUserRound, ChevronDown } from "lucide-vue-next";
import BaseIconButton from "./BaseIconButton.vue";
import Notifikasi from "./Notifikasi.vue";


const authStore = useAuthStore();
const menuStore = useMenuStore();
const router = useRouter();
const route = useRoute();

const isUserReady = computed(() => !!authStore.user);

const FILE_BASE = String(import.meta.env.VITE_FILE_BASE_URL || '').replace(/\/$/, '');
const DEFAULT_AVATAR = "/default-avatar.png";
const isAbsoluteUrl = (u) => /^https?:\/\//i.test(u || "");
const resolveAvatar = (file_user) => {
  if (!file_user) return DEFAULT_AVATAR;
  if (isAbsoluteUrl(file_user)) return file_user;
  // hindari double /storage dan double slash
  const path = String(file_user).replace(/^\/+/, "");
  return `${FILE_BASE}/${path}`;
};

const userAvatar = computed(() => {
  return resolveAvatar(authStore.user?.file_user);
});

function fallbackAvatar(e) {
  if (e?.target) e.target.src = DEFAULT_AVATAR
}

const isUserDropdownOpen = ref(false);
const activeMenu = ref(null);           // Nama menu/submenu yang aktif
const openDropdownMenu = ref(null);     // Menu dropdown level-1 yang terbuka
const openSubmenu = ref(null);          // Submenu level-2 yang terbuka (untuk fly-out)

// Ambil menu dari store
onMounted(async () => {
  if (authStore.isAuthenticated) {
    await menuStore.fetchMenus();
    updateActiveMenuFromRoute(route.path);
  }
});

// Tutup dropdown saat klik di luar navbar
function handleClickOutside(event) {
  if (
    !event.target.closest(".nav-dropdown") &&
    !event.target.closest(".user-dropdown")
  ) {
    openDropdownMenu.value = null;
    openSubmenu.value = null;
    isUserDropdownOpen.value = false;
  }
}
//tambahan abin 


onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

// Toggle dropdown menu navbar (level-1)
const toggleDropdown = (menuName) => {
  openDropdownMenu.value =
    openDropdownMenu.value === menuName ? null : menuName;
  if (openDropdownMenu.value === null) {
    openSubmenu.value = null;
  }
  activeMenu.value = menuName;
};

// Toggle submenu (level-2) untuk fly-out level-3
const toggleSubmenu = (id) => {
  openSubmenu.value = openSubmenu.value === id ? null : id;
};

// Toggle dropdown user
const toggleUserDropdown = () => {
  isUserDropdownOpen.value = !isUserDropdownOpen.value;
};

// Set active menu dan tutup dropdown
const setActiveMenu = (menuName) => {
  activeMenu.value = menuName;
  openDropdownMenu.value = null;
  openSubmenu.value = null;
};

// Logout
const logout = async () => {
  await authStore.logout()
  router.push("/login");
}

// Buat slug jadi path router-link
const formatPath = (slug) => {
  if (!slug) return null;
  return slug.startsWith("/") ? slug : `/${slug}`;
};

// Baca menu dari store dan mapping slug -> path
// const navMenus = computed(() =>
//   menuStore.menus.map((menu) => ({
//     ...menu,
//     path: formatPath(menu.slug),
//     children: (menu.children || []).map((sub) => ({
//       ...sub,
//       path: formatPath(sub.slug),
//       children: (sub.children || []).map((child) => ({
//         ...child,
//         path: formatPath(child.slug),
//       })),
//     })),
//   }))
// );
//ubah nav menu abin
// NavBar.vue (script)
const safeMenus = computed(() => Array.isArray(menuStore.menus) ? menuStore.menus : []);

// const navMenus = computed(() =>
//   safeMenus.value.map((menu) => ({
//     ...menu,
//     path: formatPath(menu.slug),
//     children: Array.isArray(menu.children) ? menu.children.map((sub) => ({
//       ...sub,
//       path: formatPath(sub.slug),
//       children: Array.isArray(sub.children) ? sub.children.map((child) => ({
//         ...child,
//         path: formatPath(child.slug),
//       })) : [],
//     })) : [],
//   }))
// );
watch(
  () => authStore.isLoggedIn,
  async (isLoggedIn) => {
    if (isLoggedIn) {
      console.log('[NavBar] User logged in, fetching menus')
      await menuStore.fetchMenus()
      updateActiveMenuFromRoute(route.path)
    }
  },
  { immediate: true }
)

// Perbaiki computed navMenus dengan guard
const navMenus = computed(() => {
  const menus = menuStore.menus || []
  console.log('[NavBar] Rendering menus:', menus.length)
  
  return menus.map(menu => ({
    ...menu,
    path: formatPath(menu.slug),
    children: Array.isArray(menu.children) 
      ? menu.children.map(sub => ({
          ...sub,
          path: formatPath(sub.slug),
          children: Array.isArray(sub.children)
            ? sub.children.map(child => ({
                ...child,
                path: formatPath(child.slug)
              }))
            : []
        }))
      : []
  }))
})

// ketika render, jangan akses .length tanpa guard

// Perbarui active menu saat route berubah
const updateActiveMenuFromRoute = (path) => {
  for (const menu of navMenus.value) {
    if (menu.path === path) {
      activeMenu.value = menu.name;
      openDropdownMenu.value = null;
      openSubmenu.value = null;
      return;
    }
    for (const sub of menu.children) {
      if (sub.path === path) {
        activeMenu.value = menu.name;
        openDropdownMenu.value = null;
        openSubmenu.value = null;
        return;
      }
      for (const child of sub.children) {
        if (child.path === path) {
          activeMenu.value = menu.name;
          openDropdownMenu.value = null;
          openSubmenu.value = null;
          return;
        }
      }
    }
  }
};

// watch(
//   () => route.path,
//   (newPath) => {
//     updateActiveMenuFromRoute(newPath);
//   }
// );


</script>

<template>
  <header class="w-full fixed top-0 left-0 z-[9999999]">
    <!-- Layer Atas -->
    <div class="h-[80px] bg-[#2F3547] flex items-center justify-between px-[50px]">
      <!-- Logo -->
      <div class="flex items-center space-x-3">
        <img src="/sipa-43.png" alt="Logo" class="h-10" />
        <img src="/logo-dsda.png" alt="Logo" class="h-10" />
      </div>
<div class="flex justify-end items-center space-x-3">
   <Notifikasi />

      <!-- User Button -->
      <div v-if="isUserReady" class="relative user-dropdown">
        <button
          @click.stop="toggleUserDropdown"
          class="flex items-center space-x-2 px-4 py-2 bg-[#0B5EC9] text-white rounded-md hover:bg-blue-700 transition"
        >
         <img
    :src="userAvatar"
    alt="Avatar"
    class="w-5 h-5 rounded-full object-cover"
    @error="fallbackAvatar"
  />
          <!-- <CircleUserRound v-else class="w-5 h-5" /> -->
          <span class="text-sm font-medium">
            {{ authStore.user?.name?.split(' ')[0] || authStore.user?.username || 'User' }}
          </span>
        </button>

        <!-- Dropdown User -->
        <div
          v-if="isUserDropdownOpen"
          class="absolute top-12 right-0 w-52 bg-white rounded-lg shadow-lg z-50"
        >
          <ul class="py-2 text-gray-700">
            <li>
              <router-link
                to="/profile"
                class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                @click="isUserDropdownOpen = false"
              >
                Profile Saya
              </router-link>
            </li>
            <li>
              <router-link
                to="/change-password"
                class="block w-full text-left px-4 py-2 hover:bg-gray-100"
                @click="isUserDropdownOpen = false"
              >
                Ubah Password
              </router-link>
            </li>
            <li>
              <button
                @click="logout"
                class="w-full text-left font-semibold px-4 py-2 text-red-600 hover:bg-red-100"
              >
                Logout
              </button>
            </li>
          </ul>
        </div>
      </div>
      </div>
    </div>

    <!-- Layer Bawah (Navbar) -->
    <div class="h-[60px] bg-white shadow-md flex items-center justify-center px-[50px]">
      <nav class="flex space-x-2 text-gray-700 text-sm font-medium">
        <div
          v-for="menu in navMenus"
          :key="menu.id"
          class="relative nav-dropdown"
        >
          <!-- Level 1 -->
          <component
            :is="menu.path ? 'router-link' : 'button'"
            :to="menu.path || undefined"
            @click.stop="toggleDropdown(menu.name)"
            class="flex items-center space-x-2 px-3 py-2 rounded-md transition"
            :class="activeMenu === menu.name
              ? 'bg-blue-100 text-blue-600'
              : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'"
          >
            <!-- Render icon dari endpoint -->
            <span v-if="menu.icon" v-html="menu.icon"></span>
            <span>{{ menu.name }}</span>
            <!-- <ChevronDown v-if="menu.children.length" class="w-4 h-4" /> -->
             <ChevronDown v-if="menu.children && menu.children.length" class="w-4 h-4" />

          </component>

          <!-- Dropdown (Level 2, dengan fly-out Level 3) -->
          <div
           v-if="menu.children && menu.children.length && openDropdownMenu === menu.name"
            class="absolute top-full mt-1 left-0 w-60 bg-white rounded-md shadow-lg py-2 z-50 border-t-2 border-blue-600"
          >
            <div
              v-for="sub in menu.children"
              :key="sub.id"
              class="relative"
            >
              <!-- Submenu item (Level 2) -->
              <!-- Jika punya children => tombol buka fly-out -->
              <button
                v-if="sub.children && sub.children.length"
                @click.stop="toggleSubmenu(sub.id)"
                class="w-full flex items-center justify-between px-4 py-2 text-left
                       text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
              >
                <span>{{ sub.name }}</span>
                <!-- chevron kanan -->
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <!-- Jika TIDAK punya children dan ADA path => langsung link -->
              <router-link
                v-else-if="sub.path"
                :to="sub.path"
                class="block w-full px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition"
                @click="setActiveMenu(sub.name)"
              >
                {{ sub.name }}
              </router-link>

              <!-- Jika TIDAK punya children dan TIDAK ada path => disabled -->
              <div
                v-else
                class="block w-full px-4 py-2 text-gray-400 bg-gray-100 cursor-default select-none"
              >
                {{ sub.name }}
              </div>

              <!-- Fly-out Level 3 -->
              <!-- Fly-out Level 3 -->
              <div
                v-if="sub.children && sub.children.length && openSubmenu === sub.id"
                class="absolute top-0 left-full w-56 bg-white rounded-md shadow-lg py-2 z-50"
              >
                <router-link
                  v-for="child in sub.children"
                  :key="child.id"
                  :to="child.path"
                  class="block w-full px-4 py-2 text-gray-600 hover:bg-blue-50 hover:text-blue-600 transition"
                  @click="setActiveMenu(child.name)"
                >
                  {{ child.name }}
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>
